import { type CommonImporterError } from '../components/ImporterErrorStage/CommonImporterErrorStage'
import { type ImporterFieldNameMap } from '../types'

export interface ErrorField {
  message: string
  code: string
  value: string
}

export interface ErrorRow {
  errors_with_codes?: {
    [key: string]: ErrorField
  }
  errors?: {
    [key: string]: string
  }
  data?: { csv_line_number: number }
}

export const formatErrorsByErrorCode = (
  errorRows: ErrorRow[],
  fieldNameMapper: ImporterFieldNameMap
): CommonImporterError[][] => {
  const errorGroups: Record<string, CommonImporterError[]> = {}

  errorRows.forEach((errorRow: ErrorRow) => {
    const { errors_with_codes: errorsWithCodes, errors } = errorRow
    const csvLineNumber = errorRow.data?.csv_line_number

    const addFieldError = (
      errorField: string,
      { message = '', code: errorCode = '', value = '' }: Partial<ErrorField>
    ) => {
      const fieldError: CommonImporterError = {
        message,
        errorCode,
        value,
        field: fieldNameMapper?.[errorField]
          ? fieldNameMapper[errorField]
          : errorField,
        csvLineIndex: csvLineNumber,
        errorKey: `${csvLineNumber}__${errorField}`,
      }

      // group errors by error code and field column
      const groupKey = `${errorCode}_${errorField}`
      if (errorGroups[groupKey]) {
        errorGroups[groupKey].push(fieldError)
      } else {
        errorGroups[groupKey] = [fieldError]
      }
    }

    if (errorsWithCodes) {
      Object.keys(errorsWithCodes).forEach((errorField) => {
        addFieldError(errorField, errorsWithCodes[errorField] || {})
      })
    } else if (errors) {
      // Some backend validation paths (e.g. the vendor mold's second pass)
      // only return the plain field -> message map without error codes;
      // render those rows instead of silently dropping them [PLAT-4979]
      Object.keys(errors).forEach((errorField) => {
        addFieldError(errorField, { message: errors[errorField] })
      })
    }
  })

  return Object.values(errorGroups)
}

export default formatErrorsByErrorCode
